<script setup lang="ts">
import type { SearchResult } from "~/types/contentSearch"

const props = defineProps<{
  result: SearchResult
  isFree: boolean
  template: "article" | "video"
}>()

const showImageField = computed(
  () => props.template === "video" || props.result.article.img,
)
</script>

<template>
  <div
    class="flex w-full flex-col truncate text-wrap p-4 md:px-6 md:py-5"
  >
    <div class="mb-4 flex gap-3 md:mb-3">
      <UContentFreeLabel v-if="isFree" />
      <slot name="badge" />
    </div>

    <div class="flex flex-col-reverse gap-1" :class="{ 'text-white': template === 'video' }">
      <h4 class="font-national2condensed text-xl/[24px] font-medium md:text-2xl/[28px]" v-html="result.title" />
      <h5 class="font-national2 text-sm md:text-base/[24px]" :class="{ 'text-[--theme-dark]': template === 'article' }">
        <span v-if="result.folder">{{ result.folder.priority }}. {{ result.folder.title }} / </span>
        <span class="uppercase">{{ result.subject.priority }}. {{ result.subject.title }}</span>
      </h5>
    </div>

    <p v-if="result.hasAccess">
      <URichText
        class="mt-2 text-sm md:mt-4 md:text-base/[24px]"
        :html="result.description"
      />
    </p>
    <UPreviewPaywall
      v-else
      class="mt-4 md:mt-6"
      :title="result.title"
      :dark="template === 'video'"
    />
  </div>

  <UIcon
    v-if="!showImageField && !result.hasAccess"
    name="lock-filled"
    size="lg"
    class="absolute bottom-4 right-4 text-darker md:bottom-auto md:top-4"
  />
  <div
    v-else-if="showImageField"
    class="relative h-36 rounded-b-2xl md:flex md:h-auto md:w-44 md:shrink-0 md:rounded-l-none md:rounded-r-2xl"
    :class="{
      'bg-[--theme-translucent-darkest]': template === 'video',
      'md:h-auto': !result.hasAccess,
    }"
  >
    <img
      v-if="result.article.img"
      :src="result.article.img.src"
      :alt="result.article.img.alt || ''"
      class="size-full rounded-b-2xl object-cover md:rounded-l-none md:rounded-r-2xl"
      :class="{
        'brightness-50': !result.hasAccess,
      }"
    />
    <div
      class="align-center absolute inset-0 flex p-4"
      :class="{
        'justify-end': template === 'article',
        'justify-between': !result.hasAccess && template === 'video',
        'md:justify-end': result.hasAccess,
      }"
    >
      <slot name="secondaryBadge" />
      <UIcon
        v-if="!result.hasAccess"
        name="lock-filled"
        class="absolute right-4 top-[-2.7rem] md:static"
        :class="{
          'text-white': template === 'video',
          'text-darker': template === 'article',
          'md:text-white': template === 'article' && result.article.img?.src,
        }"
        size="lg"
      />
    </div>
  </div>
</template>
